import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Webhook, Play, Search, CheckCircle2, XCircle, Link2 } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/use-toast';
import { cn } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

interface IntegrationApp {
  id: string;
  name: string;
  description: string;
  logo_url?: string;
  category: string;
  enabled: boolean;
  webhook_support: boolean;
  supported_events: string[];
}

interface WebhookHook {
  id: number;
  hook_type: string;
  app_id?: string;
  webhook_url?: string;
  events: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface TestResult {
  success: boolean;
  status_code?: number;
  response_body?: string;
  error?: string;
  message: string;
}

const LOCAL_APP_LOGOS: Record<string, string> = {
  slack: '/icons/integrations/slack.svg',
  zapier: '/icons/integrations/zapier.svg',
  dialogflow: '/icons/integrations/dialogflow.svg',
  webhook: '/icons/integrations/webhook.svg',
};

const PLACEHOLDER_LOGO = '/icons/integrations/placeholder.svg';

const getAppLogoSrc = (app?: { id?: string; logo_url?: string | null } | null) => {
  if (!app) return PLACEHOLDER_LOGO;
  return LOCAL_APP_LOGOS[app.id || ''] || app.logo_url || PLACEHOLDER_LOGO;
};

const AppLogo: React.FC<{
  app?: { id?: string; name?: string; logo_url?: string | null } | null;
  className?: string;
  fallbackClassName?: string;
}> = ({ app, className, fallbackClassName }) => {
  const name = app?.name || 'App';
  const initials = name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Avatar className={cn('h-10 w-10 rounded-lg shrink-0', className)}>
      <AvatarImage src={getAppLogoSrc(app)} alt={name} className="object-cover" />
      <AvatarFallback className={cn('rounded-lg bg-muted text-muted-foreground font-semibold', fallbackClassName)}>
        {initials || <Webhook className="h-4 w-4" />}
      </AvatarFallback>
    </Avatar>
  );
};

const IntegrationsPage: React.FC = () => {
  const [apps, setApps] = useState<IntegrationApp[]>([]);
  const [hooks, setHooks] = useState<WebhookHook[]>([]);
  const [, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHook, setEditingHook] = useState<WebhookHook | null>(null);
  const [selectedApp, setSelectedApp] = useState<IntegrationApp | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testingHookId, setTestingHookId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WebhookHook | null>(null);
  const [webhookSearch, setWebhookSearch] = useState('');

  const [formData, setFormData] = useState({
    webhook_url: '',
    events: [] as string[],
    app_id: '',
    settings: {},
  });

  const availableEvents = [
    { id: 'conversation_created', label: 'Conversation Created', category: 'Conversations' },
    { id: 'conversation_updated', label: 'Conversation Updated', category: 'Conversations' },
    { id: 'conversation_resolved', label: 'Conversation Resolved', category: 'Conversations' },
    { id: 'conversation_reopened', label: 'Conversation Reopened', category: 'Conversations' },
    { id: 'conversation_status_changed', label: 'Status Changed', category: 'Conversations' },
    { id: 'conversation_assigned', label: 'Conversation Assigned', category: 'Conversations' },
    { id: 'conversation_unassigned', label: 'Conversation Unassigned', category: 'Conversations' },
    { id: 'message_created', label: 'Message Created', category: 'Messages' },
    { id: 'message_updated', label: 'Message Updated', category: 'Messages' },
    { id: 'contact_created', label: 'Contact Created', category: 'Contacts' },
    { id: 'contact_updated', label: 'Contact Updated', category: 'Contacts' },
    { id: 'agent_added', label: 'Agent Added', category: 'Agents' },
    { id: 'agent_removed', label: 'Agent Removed', category: 'Agents' },
  ];

  const loadApps = async () => {
    try {
      const response = await api.get('/integrations/apps');
      setApps(response.data);
    } catch (error) {
      console.error('Failed to load integration apps:', error);
      toast({ title: 'Error', description: 'Failed to load integration apps', variant: 'destructive' });
    }
  };

  const loadHooks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/integrations/hooks');
      setHooks(response.data);
    } catch (error) {
      console.error('Failed to load integration hooks:', error);
      toast({ title: 'Error', description: 'Failed to load integration hooks', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHook = async () => {
    try {
      const response = await api.post('/integrations/hooks', formData);
      setHooks([...hooks, response.data]);
      setDialogOpen(false);
      resetForm();
      toast({ title: 'Webhook created', description: 'Integration hook created successfully' });
    } catch (error) {
      console.error('Failed to create integration hook:', error);
      toast({ title: 'Error', description: 'Failed to create integration hook', variant: 'destructive' });
    }
  };

  const handleUpdateHook = async () => {
    if (!editingHook) return;
    try {
      const response = await api.put(`/integrations/hooks/${editingHook.id}`, formData);
      setHooks(hooks.map((hook) => (hook.id === editingHook.id ? response.data : hook)));
      setDialogOpen(false);
      setEditingHook(null);
      resetForm();
      toast({ title: 'Webhook updated', description: 'Integration hook updated successfully' });
    } catch (error) {
      console.error('Failed to update integration hook:', error);
      toast({ title: 'Error', description: 'Failed to update integration hook', variant: 'destructive' });
    }
  };

  const handleDeleteHook = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/integrations/hooks/${deleteTarget.id}`);
      setHooks(hooks.filter((hook) => hook.id !== deleteTarget.id));
      toast({ title: 'Webhook deleted', description: 'Integration hook deleted successfully' });
    } catch (error) {
      console.error('Failed to delete integration hook:', error);
      toast({ title: 'Error', description: 'Failed to delete integration hook', variant: 'destructive' });
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleTestHook = async (hookId: number) => {
    setTestingHookId(hookId);
    setTestResult(null);
    try {
      const response = await api.post(`/integrations/hooks/${hookId}/test`);
      setTestResult(response.data);
    } catch (error) {
      console.error('Failed to test integration hook:', error);
      toast({ title: 'Error', description: 'Failed to test integration hook', variant: 'destructive' });
    } finally {
      setTestingHookId(null);
    }
  };

  const openCreateDialog = (app?: IntegrationApp) => {
    resetForm();
    if (app) {
      setSelectedApp(app);
      setFormData((prev) => ({ ...prev, app_id: app.id }));
    }
    setEditingHook(null);
    setTestResult(null);
    setDialogOpen(true);
  };

  const openEditDialog = (hook: WebhookHook) => {
    setFormData({
      webhook_url: hook.webhook_url || '',
      events: hook.events || [],
      app_id: hook.app_id || '',
      settings: {},
    });
    setEditingHook(hook);
    setSelectedApp(apps.find((app) => app.id === hook.app_id) || null);
    setTestResult(null);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ webhook_url: '', events: [], app_id: '', settings: {} });
    setSelectedApp(null);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEventToggle = (eventId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      events: checked ? [...prev.events, eventId] : prev.events.filter((id) => id !== eventId),
    }));
  };

  const groupEventsByCategory = () => {
    const categories: Record<string, typeof availableEvents> = {};
    availableEvents.forEach((event) => {
      if (!categories[event.category]) categories[event.category] = [];
      categories[event.category].push(event);
    });
    return categories;
  };

  const handleSelectAllInCategory = (categoryEvents: typeof availableEvents, selectAll: boolean) => {
    const ids = categoryEvents
      .filter((e) => !selectedApp || selectedApp.supported_events.includes(e.id))
      .map((e) => e.id);
    if (selectAll) {
      setFormData((prev) => ({
        ...prev,
        events: Array.from(new Set([...prev.events, ...ids])),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        events: prev.events.filter((id) => !ids.includes(id)),
      }));
    }
  };

  const filteredHooks = hooks.filter((hook) => {
    if (!webhookSearch) return true;
    const q = webhookSearch.toLowerCase();
    return (
      (hook.webhook_url || '').toLowerCase().includes(q) ||
      (hook.app_id || '').toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    loadApps();
    loadHooks();
  }, []);

  return (
    <TooltipProvider>
      <div className="container mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Integrations</h1>
          <p className="text-sm text-muted-foreground">
            Connect your workspace with external apps and webhooks
          </p>
        </div>

        <Tabs defaultValue="apps">
          <TabsList>
            <TabsTrigger value="apps">Available Apps</TabsTrigger>
            <TabsTrigger value="webhooks">
              Configured Webhooks
              {hooks.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 min-w-5 text-xs">
                  {hooks.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Apps Tab ── */}
          <TabsContent value="apps" className="mt-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Available Integration Apps</h2>

              {apps.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                    <div className="rounded-full bg-muted p-4">
                      <Webhook className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="font-medium">No integration apps available</p>
                    <p className="text-sm text-muted-foreground max-w-xs">
                      Integration apps will appear here once they are added to your workspace.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {apps.map((app) => (
                    <Card
                      key={app.id}
                      className="group relative overflow-hidden transition-shadow hover:shadow-md border-t-2 border-t-primary/20 hover:border-t-primary"
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <AppLogo app={app} />
                            <div>
                              <CardTitle className="text-sm font-semibold">{app.name}</CardTitle>
                              <Badge variant="outline" className="text-xs mt-1">
                                {app.category}
                              </Badge>
                            </div>
                          </div>
                          {app.enabled && (
                            <Badge
                              variant="outline"
                              className="text-xs shrink-0 border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                            >
                              Active
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <CardDescription className="text-sm line-clamp-2">{app.description}</CardDescription>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {app.webhook_support && (
                            <Badge variant="secondary" className="text-xs">Webhooks</Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {app.supported_events.length} events
                          </Badge>
                        </div>
                      </CardContent>
                      <Separator />
                      <CardFooter className="pt-3">
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => openCreateDialog(app)}
                          disabled={!app.webhook_support}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Configure Webhook
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Webhooks Tab ── */}
          <TabsContent value="webhooks" className="mt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search webhooks..."
                    value={webhookSearch}
                    onChange={(e) => setWebhookSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button onClick={() => openCreateDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Webhook
                </Button>
              </div>

              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>App</TableHead>
                        <TableHead>Webhook URL</TableHead>
                        <TableHead>Events</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredHooks.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                              <div className="rounded-full bg-muted p-3">
                                <Link2 className="h-6 w-6 text-muted-foreground" />
                              </div>
                              <p className="font-medium text-sm">
                                {webhookSearch ? 'No webhooks match your search' : 'No webhooks configured yet'}
                              </p>
                              {!webhookSearch && (
                                <p className="text-xs text-muted-foreground">
                                  Add your first webhook to start receiving events
                                </p>
                              )}
                              {!webhookSearch && (
                                <Button size="sm" onClick={() => openCreateDialog()}>
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Your First Webhook
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      {filteredHooks.map((hook) => (
                        <TableRow key={hook.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <AppLogo
                                app={
                                  hook.app_id
                                    ? apps.find((a) => a.id === hook.app_id) || {
                                        id: hook.app_id,
                                        name: hook.app_id,
                                      }
                                    : { id: 'webhook', name: 'Generic Webhook' }
                                }
                                className="h-8 w-8 rounded-md"
                                fallbackClassName="rounded-md"
                              />
                              <span className="text-sm font-medium">
                                {hook.app_id
                                  ? apps.find((a) => a.id === hook.app_id)?.name || hook.app_id
                                  : 'Generic Webhook'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm text-muted-foreground truncate max-w-[200px] block cursor-default font-mono text-xs">
                                  {hook.webhook_url}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="max-w-xs break-all">
                                {hook.webhook_url}
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{hook.events.length} events</span>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                hook.active
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                  : 'border-border bg-muted text-muted-foreground'
                              }
                            >
                              {hook.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleTestHook(hook.id)}
                                    disabled={testingHookId === hook.id}
                                  >
                                    <Play className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Test Webhook</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => openEditDialog(hook)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:bg-red-500/10 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/15 dark:hover:text-red-300"
                                    onClick={() => setDeleteTarget(hook)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* ── Create / Edit Dialog ── */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); setTestResult(null); } }}>
          <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[620px]">
            <DialogHeader className="shrink-0 space-y-1.5 border-b border-border px-6 py-4 pr-14 text-left">
              <DialogTitle>
                {editingHook ? 'Edit Integration Hook' : 'Create Integration Hook'}
              </DialogTitle>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
            <div className="space-y-5">
              {selectedApp && (
                <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
                  <AppLogo
                    app={selectedApp}
                    className="h-8 w-8 rounded-md"
                    fallbackClassName="rounded-md bg-primary/10 text-primary"
                  />
                  <div>
                    <p className="text-sm font-medium">{selectedApp.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedApp.category}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="webhook-url">Webhook URL <span className="text-destructive">*</span></Label>
                <Input
                  id="webhook-url"
                  value={formData.webhook_url}
                  onChange={(e) => handleFormChange('webhook_url', e.target.value)}
                  placeholder="https://your-app.com/webhooks/converse"
                />
                <p className="text-xs text-muted-foreground">
                  The URL that will receive POST requests for the selected events
                </p>
              </div>

              <Separator />

              <div className="space-y-4">
                <Label>Subscribe to Events</Label>
                {Object.entries(groupEventsByCategory()).map(([category, events]) => {
                  const filteredEvents = events.filter(
                    (e) => !selectedApp || selectedApp.supported_events.includes(e.id)
                  );
                  if (filteredEvents.length === 0) return null;
                  const allSelected = filteredEvents.every((e) => formData.events.includes(e.id));

                  return (
                    <div key={category} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">{category}</p>
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => handleSelectAllInCategory(filteredEvents, !allSelected)}
                        >
                          {allSelected ? 'Deselect all' : 'Select all'}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {filteredEvents.map((event) => {
                          const checked = formData.events.includes(event.id);
                          return (
                            <button
                              key={event.id}
                              type="button"
                              onClick={() => handleEventToggle(event.id, !checked)}
                              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                                checked
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-border bg-background text-foreground hover:bg-muted'
                              }`}
                            >
                              {checked && <CheckCircle2 className="h-3 w-3" />}
                              {event.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {formData.events.length === 0 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1.5">
                    <XCircle className="h-3.5 w-3.5" />
                    Select at least one event to subscribe to
                  </p>
                )}
              </div>

              {/* Inline test result (edit mode only) */}
              {editingHook && testResult && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Last Test Result</p>
                    <Alert variant={testResult.success ? 'default' : 'destructive'}>
                      <AlertDescription className="flex items-center gap-2">
                        {testResult.success
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                          : <XCircle className="h-4 w-4 shrink-0" />}
                        {testResult.message}
                        {testResult.status_code && (
                          <Badge variant="outline" className="ml-auto text-xs">{testResult.status_code}</Badge>
                        )}
                      </AlertDescription>
                    </Alert>
                    {testResult.response_body && (
                      <div className="rounded-md bg-muted p-3">
                        <code className="text-xs font-mono break-all">{testResult.response_body}</code>
                      </div>
                    )}
                    {testResult.error && (
                      <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
                        <code className="text-xs font-mono text-destructive break-all">{testResult.error}</code>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            </div>

            <DialogFooter className="shrink-0 gap-2 border-t border-border px-6 py-4">
              <Button variant="outline" onClick={() => { setDialogOpen(false); setTestResult(null); }}>
                Cancel
              </Button>
              {editingHook && (
                <Button
                  variant="outline"
                  onClick={() => handleTestHook(editingHook.id)}
                  disabled={testingHookId === editingHook.id}
                >
                  <Play className="h-4 w-4 mr-2" />
                  {testingHookId === editingHook.id ? 'Testing...' : 'Test'}
                </Button>
              )}
              <Button
                onClick={editingHook ? handleUpdateHook : handleCreateHook}
                disabled={!formData.webhook_url || formData.events.length === 0}
              >
                {editingHook ? 'Update Webhook' : 'Create Webhook'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Delete Confirmation ── */}
        <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Webhook</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this integration hook?
                {deleteTarget?.webhook_url && (
                  <span className="block mt-2 font-mono text-xs text-foreground truncate">
                    {deleteTarget.webhook_url}
                  </span>
                )}
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={handleDeleteHook}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

export default IntegrationsPage;
